import { NodeLayout } from '@/components/nodes/layout';
import { cn } from '@/lib/utils';
import { getIncomers, useNodeConnections, useReactFlow } from '@xyflow/react';
import { JSONPath } from 'jsonpath-plus';
import { Loader2 as Loader2Icon } from 'lucide-react';
import { memo, useEffect, useMemo, useState } from 'react';
import { getMagicRenderer } from './registry';
import { getShareBuilder } from './share-registry';
import { Button } from '@/components/ui/button';
import { Share2 as Share2Icon } from 'lucide-react';
import { useProject } from '@/providers/project';
import { toast } from 'sonner';
import { areNodePropsEqual } from '../are-node-props-equal';
import { useI18n } from '@/providers/i18n';

export type MagicNodeStyleConfig = {
	cardClassName?: string;
	contentClassName?: string;
	loadingClassName?: string;
};

export type MagicNodeSpec = {
	jsonPaths?: Record<string, string | string[]>;
	readyWhenJsonPath?: string | string[];
	style?: MagicNodeStyleConfig;
	nodeTitle?: string;
	pollIntervalMs?: number;
	layout?: {
		loadingAspectRatio?: string;
		rendererId?: string;
	};
	traversal?: {
		mode?: 'direct' | 'chain' | 'all';
		maxDepth?: number;
		maxNodes?: number;
	};
	share?: {
		builderId?: string;
	};
};

export type MagicNodeProps = {
	id: string;
	type: string;
	data: { style?: MagicNodeStyleConfig; spec?: MagicNodeSpec } & Record<string, unknown>;
};

export const MagicNode = memo(({ id, type, data }: MagicNodeProps) => {
	const { t } = useI18n();
	const { getNodes, getEdges } = useReactFlow();
	const project = useProject();
	const [payload, setPayload] = useState<Record<string, unknown>>({});
	const [ready, setReady] = useState(false);
	const [sharing, setSharing] = useState(false);

	// Perf: only re-collect upstream when the immediate connections set changes
	// (instead of every full graph mutation). The poll loop still re-runs
	// internally until `ready` flips true.
	const incomingConnections = useNodeConnections({ id, handleType: 'target' });
	const incomingSourcesKey = useMemo(
		() =>
			(incomingConnections || [])
				.map((c: any) => String(c?.source ?? ''))
				.sort()
				.join('|'),
		[incomingConnections],
	);

	useEffect(() => {
		let cancelled = false;
		let timer: ReturnType<typeof setTimeout> | null = null;

		const collectUpstream = (
			startId: string,
			mode: 'direct' | 'chain' | 'all',
			maxDepth: number,
			maxNodes: number
		) => {
			const nodes: Array<{ id: string; type: string; data: any; distance: number }> = [];
			const visited = new Set<string>();
			const queue: Array<{ id: string; distance: number }> = [];
			const pushIncomers = (nodeId: string, distance: number) => {
				const ims = getIncomers({ id: nodeId }, getNodes(), getEdges());
				for (const n of ims) {
					if (!visited.has(n.id)) queue.push({ id: n.id, distance });
				}
			};
			pushIncomers(startId, 1);
			while (queue.length && nodes.length < maxNodes) {
				const { id: currentId, distance } = queue.shift()!;
				if (visited.has(currentId)) continue;
				visited.add(currentId);
				const n = getNodes().find((x) => x.id === currentId);
				if (!n) continue;
				nodes.push({ id: n.id, type: (n as any).type ?? 'unknown', data: (n as any).data, distance });
				if (mode !== 'direct' && distance < maxDepth) {
					pushIncomers(currentId, distance + 1);
				}
			}
			nodes.sort((a, b) => a.distance - b.distance);
			return nodes.map((n, i) => ({ ...n, index: i + 1 }));
		};

		const evalJsonPath = (expr: string | string[] | undefined, dataset: any): any => {
			if (!expr) return undefined;
			const list = Array.isArray(expr) ? expr : [expr];
			for (const e of list) {
				try {
					const res = JSONPath({ path: e, json: dataset }) as any[];
					if (Array.isArray(res) && res.length) {
						const first = res.find((v) => v !== undefined && v !== null && v !== '');
						if (first !== undefined) return first;
					}
				} catch {}
			}
			return undefined;
		};

		const compute = () => {
			if (cancelled) return;
			const spec = (data?.spec ?? {}) as MagicNodeSpec;
			const mode = spec.traversal?.mode ?? 'all';
			const maxDepth = spec.traversal?.maxDepth ?? 50;
			const maxNodes = spec.traversal?.maxNodes ?? 500;
			const upstream = collectUpstream(id, mode, maxDepth, maxNodes);
			const dataset = { self: { id, type, data }, upstream };
			const computed: Record<string, unknown> = {};
			Object.keys(spec.jsonPaths ?? {}).forEach((key) => {
				const expr = spec.jsonPaths?.[key];
				(computed as any)[key] = evalJsonPath(expr as any, dataset);
			});
			setPayload(computed);
			const ok = Boolean(spec.readyWhenJsonPath ? evalJsonPath(spec.readyWhenJsonPath, dataset) : false);
			setReady(ok);
			if (!ok) {
				const interval = spec.pollIntervalMs ?? 600;
				timer = setTimeout(compute, interval);
			}
		};

		compute();
		return () => {
			cancelled = true;
			if (timer) clearTimeout(timer);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getEdges, getNodes, id, incomingSourcesKey, data?.spec]);

	const spec = (data?.spec ?? {}) as MagicNodeSpec;
	const style = { ...(spec?.style ?? {}), ...(data?.style ?? {}) } as MagicNodeStyleConfig;
	const titleLabel = spec.nodeTitle ?? ((data as any)?.nodeTitle ?? '');
	const loadingAspect = spec.layout?.loadingAspectRatio ?? '16/9';
	const renderer = useMemo(() => getMagicRenderer(spec.layout?.rendererId), [spec.layout?.rendererId]);
	const shareUrl = useMemo(() => {
		try {
			// 仅当提供了 share.builderId 时才启用分享
			if (!spec?.share?.builderId) return '';
			const origin = typeof window !== 'undefined' ? window.location.origin : '';
			const builder = getShareBuilder(spec.share.builderId);
			if (builder) return builder({ origin, projectId: project?.id ?? null, nodeId: id });
			return '';
		} catch {
			return '';
		}
	}, [project?.id, id, spec?.share?.builderId]);

	const handleSaveShare = async (): Promise<string | null> => {
		try {
			const target = document.getElementById(`share-node-${id}`);
			if (!target) throw new Error('Share container not found');
			const html = (target as HTMLElement).outerHTML;
			await fetch('/api/share', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ projectId: project?.id, nodeId: id, html, width: (target as HTMLElement | null)?.clientWidth ?? undefined }),
			});
			return shareUrl || null;
		} catch (e) {
			toast.error(t('Share failed'));
			try { console.error(e); } catch {}
			return null;
		}
	};

	return (
		<NodeLayout
			id={id}
			data={data as any}
			type={type}
			title={titleLabel}
			toolbar={[
				{
					children: (
						spec?.share?.builderId ? (
							<Button
								aria-label={t('Share')}
								size="icon"
								className="rounded-full"
								disabled={sharing || !project?.id}
								onClick={async () => {
									if (sharing) return;
									setSharing(true);
									const url = await handleSaveShare();
									setSharing(false);
									try { if (typeof window !== 'undefined' && url) window.open(url, '_blank', 'noopener,noreferrer'); } catch {}
								}}
								title={t('Share')}
							>
								{sharing ? (
									<Loader2Icon aria-label={t('Sharing')} className="animate-spin" size={12} />
								) : (
									<Share2Icon size={12} />
								)}
							</Button>
						) : null
					),
				},
			]}
		>
			<div id={`share-node-${id}`}>
				{!ready ? (
					<div className={cn('relative w-full', style.loadingClassName)} style={{ aspectRatio: loadingAspect }}>
						<div className="relative flex h-full items-center justify-center p-6">
							<Loader2Icon aria-label={t('Loading')} className="size-6 animate-spin text-muted-foreground" />
						</div>
					</div>
				) : renderer ? (
					(() => { const Renderer = renderer!; return <Renderer payload={payload} style={style} spec={spec} />; })()
				) : (
					<div className={cn('relative w-full', style.cardClassName)}>
						<div className={cn('p-4', style.contentClassName)}>
							<div className="text-xs text-muted-foreground">{t('Magic node failed to render')}</div>
						</div>
					</div>
				)}
			</div>
		</NodeLayout>
	);
}, areNodePropsEqual);
MagicNode.displayName = 'MagicNode';

export default MagicNode;